import { Link, A } from '@solidjs/router';
import clsx from 'clsx';
import { For, Show } from 'solid-js';
import {  useAppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { Heading } from '../../ui-components/heading/heading';
import { Text } from '../../ui-components/text/text';
import { WrappedImage } from '../image-component/wrapped-image';
import { BlogPostIntroProps, CategoryType } from './blog-post-intro.types';
import tailwindConfig from '../../../tailwind.config';
import { BlogPostAuthor } from './blog-post-author';
import { SinglePostSchema } from '../schema/single-post';

export const BlogPostIntroContainer = (props: BlogPostIntroProps) => {
    const { pageBlogArchiveUrl, viewport, translate } = useAppContext();

    const isMobile = () => viewport.width <= tailwindConfig.theme.extend.screenValues.tabletValue;

    return (
        <ErrorCatcher componentName="BlogPost">
            <section class="px-containerPadding">
                <div class="max-w-wide mx-auto">
                    <div class="flex gap-4 text-neutral-700">
                        {props.readingTime ? (
                            <>
                                <Text tag="span" disableMargins={true}>
                                    {`${props.readingTime} ${translate('minute-read')}`}
                                </Text>
                            </>
                        ) : null}
                    </div>
                    <div class="mb-8 mt-4">
                        <Heading disableMaxWidth={true} tag="h1" classes="mt-0" fontSize="xxl">
                            {props.postTitle}
                        </Heading>
                    </div>
                    <Text disableMaxWidth={true} fontSize="xl">
                        {props.preamble}
                    </Text>
                    <div class="mb-8"></div>
                    <Show when={props.postType === 'personal'}>
                        <div class="border-b-1 border-primary w-full text-center leading-none">
                            <span class={clsx('text-paragraphXS leading-none bg-primary-100 p-2 translate-y-[14px] inline-block')}>
                                {translate('written-by')}
                            </span>
                        </div>
                    </Show>

                    <div class="tablet:mb-8 mb-xl">
                        <div
                            class={clsx(
                                'tablet:py-8 flex',
                                { 'flex-row': props.postType === 'corporate' },
                                { 'flex-col': props.postType === 'personal' },
                                ' tablet:flex-row tablet:items-center gap-xs flex-wrap items-center py-0',
                            )}
                        >
                            <Show when={props.author.link?.url} fallback={(
                                <div>
                                    <BlogPostAuthor
                                        author={props.author}
                                        date={props.date}
                                        postType={props.postType}
                                        isMobile={isMobile}
                                    />
                                </div>
                            )}>
                                <A href={props.author.link?.url || '/'} target={props.author.link?.target}>
                                    <BlogPostAuthor
                                        author={props.author}
                                        date={props.date}
                                        postType={props.postType}
                                        isMobile={isMobile}
                                    />
                                </A>
                            </Show>

                            {pageBlogArchiveUrl && (
                                <div class="tablet:ml-auto ml-0 flex flex-wrap items-center gap-2">
                                    <For each={props?.categories}>
                                        {(category: CategoryType) => (
                                            <>
                                                <Link  href={pageBlogArchiveUrl + '?category=' + category.slug}>
                                                    <span innerHTML={category.name} class="border-1 border-primary-600 px-s text-primary-600 hover:bg-primary-600 hover:text-primary-100 text-medium inline-block rounded-l py-[0.31rem] transition-colors duration-200"/>
                                                </Link>
                                            </>
                                        )}
                                    </For>
                                </div>
                            )}
                        </div>
                    </div>
                    <div class="tablet:mb-12 mb-4">
                        {props?.featuredImage && <WrappedImage image={props.featuredImage} aspectRatio="original" width="1000px" height="auto" />}
                    </div>
                </div>
            </section>
            <SinglePostSchema
                postType="BlogPosting"
                content={props}
                hasAuthor={true}
            />
        </ErrorCatcher>
    );
};
